import {
  getLocaleWithCountry,
  type ILanguage,
  languageMap,
} from '@booking-portal/config/i18n-config';
import { useAppStore } from '@booking-portal/stores/app-store';
import type { ILocationConfigItem } from '@booking-portal/types';
import dayjs from 'dayjs';
import { isArray, isNumber } from 'lodash-es';
import type { Maybe } from 'shared/types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { LocationQueryValue } from 'vue-router';

import {
  formatDate,
  formatDateRange,
  formatDateTime,
  formatISODate,
} from './date-utils';

export function getFormatCurrency(
  amount: number | null | undefined,
  userLocale: ILanguage = 'en',
  currencyCode = 'EUR'
): string {
  if (amount == null) {
    return '';
  }

  const format = (languageMap[userLocale] || languageMap.en) as {
    locale: string;
  };

  try {
    const formatter = new Intl.NumberFormat(format.locale, {
      style: 'currency',
      currency: currencyCode,
    });

    return formatter.format(amount);
  } catch {
    const formatter = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
    });

    return formatter.format(amount);
  }
}

export function getFormatCompactCurrency(
  amount: number | null | undefined,
  userLocale: ILanguage = 'en',
  currencyCode = 'EUR'
): string {
  if (amount == null) {
    return '';
  }

  const format = (languageMap[userLocale] || languageMap.en) as {
    locale: string;
  };
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  };

  try {
    return new Intl.NumberFormat(format.locale, options).format(amount);
  } catch {
    return new Intl.NumberFormat('en', options).format(amount);
  }
}

export interface DiscountPriceInput {
  finalPrice?: number | null;
  discountedFinalPrice?: number | null;
  discountPercentage?: number | null;
  hasDiscount?: boolean | null;
}

export interface DiscountPriceInfo {
  originalPrice: number;
  discountedPrice: number;
  hasAdvertisedDiscount: boolean;
  hasManualDiscount: boolean;
  hasDiscount: boolean;
}

export function getDiscountPriceInfo(
  input: DiscountPriceInput
): DiscountPriceInfo {
  const { discountedFinalPrice } = input;
  const finalPrice = input.finalPrice ?? 0;
  const discountPct = input.discountPercentage ?? 0;
  const discountEnabled = input.hasDiscount ?? true;

  const originalPrice = roundToCents(finalPrice);
  const hasAdvertisedDiscount =
    discountedFinalPrice != null && discountedFinalPrice !== input.finalPrice;
  const hasManualDiscount =
    discountEnabled && discountPct > 0 && !hasAdvertisedDiscount;

  let discountedPrice: number;
  if (hasAdvertisedDiscount) {
    discountedPrice = roundToCents(discountedFinalPrice);
  } else if (hasManualDiscount) {
    discountedPrice = roundToCents(finalPrice * (1 - discountPct / 100));
  } else {
    discountedPrice = originalPrice;
  }

  return {
    originalPrice,
    discountedPrice,
    hasAdvertisedDiscount,
    hasManualDiscount,
    hasDiscount: hasAdvertisedDiscount || hasManualDiscount,
  };
}

function roundToCents(amount: number): number {
  const sign = Math.sign(amount);
  return (sign * Math.round(Math.abs(amount) * 100)) / 100;
}

export function formatDecimalNumber(amount: number, locale: string) {
  const formatter = new Intl.NumberFormat(locale);
  return formatter.format(amount);
}

export function formatPercentage(
  amount: number,
  userLocale: ILanguage = 'en'
): string {
  const format = (languageMap[userLocale] || languageMap.en) as {
    locale: string;
  };

  try {
    const formatter = new Intl.NumberFormat(format.locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(amount / 100);
  } catch {
    // simple fallback
    const formatter = new Intl.NumberFormat('en', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formatter.format(amount / 100);
  }
}

export function formatAddress(
  data: ILocationConfigItem | null | undefined
): string {
  let str = '';

  if (!data) {
    return str;
  }

  if (data.street) {
    str = data.street;
    if (data.postalCode || data.city) {
      str += ',';
      if (data.postalCode) {
        str += ` ${data.postalCode}`;
      }
      if (data.city) {
        str += ` ${data.city}`;
      }
    }
  } else if (data.postalCode) {
    str = data.postalCode;
    if (data.city) {
      str += ` ${data.city}`;
    }
  } else if (data.city) {
    return data.city;
  }
  return str;
}

export type MaybeDate = string | Date | null | undefined;

export function useLocalFormatter() {
  const { locale } = useI18n();
  const store = useAppStore();

  return {
    locale,
    localeWithCountry: computed(() => {
      return getLocaleWithCountry(locale.value);
    }),
    formatDecimalNumber(amount: number) {
      return formatDecimalNumber(amount, locale.value);
    },
    formatCurrency(
      amount: number,
      currencyCode = store.location?.currency.code
    ) {
      return getFormatCurrency(amount, locale.value as ILanguage, currencyCode);
    },
    formatCompactCurrency(
      amount: number,
      currencyCode = store.location?.currency.code
    ) {
      return getFormatCompactCurrency(
        amount,
        locale.value as ILanguage,
        currencyCode
      );
    },
    formatPercentage(amount: number) {
      return formatPercentage(amount, locale.value as ILanguage);
    },
    formatDate(date: MaybeDate) {
      return formatDate(date, locale.value);
    },
    formatDateRange(from: MaybeDate, to?: MaybeDate) {
      return formatDateRange(from, to, locale.value);
    },
    formatDateTime(date: MaybeDate) {
      return formatDateTime(date, locale.value);
    },
    formatShortDate(date: MaybeDate) {
      return date && dayjs(date).isValid()
        ? dayjs(date).locale(locale.value).format('MMM D, YY')
        : '';
    },
    formatISODate,

    getMonthName(date: MaybeDate) {
      return dayjs(date).locale(locale.value).format('MMMM');
    },
  };
}

export function parseToInteger<TFallback = number>(
  value: Maybe<string | number | LocationQueryValue | LocationQueryValue[]>,
  fallback: TFallback
) {
  if (isNumber(value)) {
    return value;
  }

  if (value == null) {
    return fallback;
  }

  let candidate: Maybe<string>;

  if (isArray(value)) {
    if (value.length <= 0) {
      return fallback;
    }

    candidate = value[0];
  } else {
    candidate = value;
  }

  if (!candidate) {
    return fallback;
  }

  const parsed = parseInt(candidate.trim());

  if (isNaN(parsed)) {
    return fallback;
  }

  return parsed;
}

export function parseToString<TFallback = number>(
  value: Maybe<string | LocationQueryValue | LocationQueryValue[]>,
  fallback: TFallback
) {
  if (value == null) {
    return fallback;
  }

  let candidate: Maybe<string>;

  if (isArray(value)) {
    if (value.length <= 0) {
      return fallback;
    }

    candidate = value[0];
  } else {
    candidate = value;
  }

  if (candidate == null) {
    return fallback;
  }

  return candidate;
}
